import gql from 'graphql-tag';
import { useEffect } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { useWizardContext } from 'pages/qualify/provider';
import { parseAnyListToOptionsList } from 'pages/qualify/utils';
import { WIZARD_IMAGES_PER_STEP_ID } from 'pages/qualify/wizard-images-urls';
import WizardInput from '../wizard-input';
import WizardStep from '../wizard-step';
import { Organization, StepIdsEnum } from '../../types';

export default function PhotographyStep() {
  const { tokenData, watch, setValues, values, token, organizationData } =
    useWizardContext();
  const {
    data: applicationAssetQueryData,
    loading,
    refetch,
  } = useQuery<{
    sponsorshipApplicationView: Partial<Organization>;
  }>(APPLICATION_ASSET_QUERY, {
    variables: { season: tokenData.season, year: tokenData.year },
    context: { headers: { Authorization: `Bearer ${token}` } },
    fetchPolicy: 'network-only',
  });
  const [performMutation] = useMutation(MUTATION);
  const accepted = watch('accepted');

  useEffect(() => {
    if (values.organizationId) {
      const { savedStepId } = organizationData?.sponsorshipApplication;
      if (savedStepId === StepIdsEnum.PHOTOS) return;
      const { photosDetail } =
        applicationAssetQueryData?.sponsorshipApplicationView
          .sponsorshipApplication ?? {};
      setValues({
        ...values,
        accepted:
          applicationAssetQueryData?.sponsorshipApplicationView
            .sponsorshipApplication?.photosAccepted ?? false,
        sponsorablePropertyIds:
          photosDetail?.associatedSponsorableProperties?.map(
            (property) => property.id,
          ),
        refusalReason:
          applicationAssetQueryData?.sponsorshipApplicationView
            .sponsorshipApplication?.photosRefusalReason,
      });
    }
  }, [values.organizationId, applicationAssetQueryData]);

  return (
    <WizardStep
      performMutation={performMutation}
      refetchAfterMutation={refetch}
      mutationStaticIds={{
        organizationId: tokenData.id,
        sponsorshipApplicationId:
          applicationAssetQueryData?.sponsorshipApplicationView
            ?.sponsorshipApplication?.id,
        offerId: tokenData.offer_id,
        id: applicationAssetQueryData?.sponsorshipApplicationView
          .sponsorshipApplication?.photosDetail?.capabilityDetailable?.id,
      }}
      isLoading={loading}
    >
      <WizardStep.Picture src={WIZARD_IMAGES_PER_STEP_ID[StepIdsEnum.PHOTOS]} />
      <WizardStep.Content
        title="Professional photography"
        subtitle="The game-time jersey photography session requires arranging for a TeamSnap-provided photographer to capture action shots of players wearing branded collateral during a game. Photos taken at this session will be provided to your organization afterward."
      >
        <div className="sui-grid" style={{ gap: 24 }}>
          <div className="sui-mb-3 sui-text-desktop-4">
            <p className="sui-mb-2">Your organization must:</p>
            <ul className="sui-grid sui-pl-3" style={{ gap: 8 }}>
              <li>
                Manage the collection of signed photo release forms from parents
                or players affiliated with the team that will be photographed,
                on behalf of the sponsor.
              </li>
              <li>
                Digitally obtain any releases not signed at the event within
                three weeks of the event.
              </li>
              <li>
                Provide available dates for the photographer to capture the
                sponsored jersey action shots
              </li>
            </ul>
          </div>
          <WizardInput
            type="boolean-question"
            name="accepted"
            label="My organization is capable of coordinating a photography session based on these requirements"
            helpText="Average sponsorship funding $450-$500"
          />
          {accepted && (
            <>
              <WizardInput
                type="select"
                isMulti
                name="sponsorablePropertyIds"
                label="Programs this applies to"
                rules={{ required: 'Please select at least one program' }}
                options={parseAnyListToOptionsList(
                  values.sponsorableProperties ?? [],
                  { withEmptyOption: false },
                )}
              />
            </>
          )}
          {!accepted && (
            <>
              <WizardInput
                type="textarea"
                name="refusalReason"
                label="If possible, please share your reasons and specify the amount of funding at which you might reconsider opting in to this asset"
              />
            </>
          )}
        </div>
      </WizardStep.Content>
    </WizardStep>
  );
}

const APPLICATION_ASSET_QUERY = gql`
  query SponsorshipApplicationView($season: String!, $year: Int!) {
    sponsorshipApplicationView {
      id
      sponsorshipApplication(season: $season, year: $year) {
        id
        photosAccepted
        photosRefusalReason
        photosDetail {
          id
          associatedSponsorableProperties {
            id
            name
          }
        }
      }
    }
  }
`;

const MUTATION = gql`
  mutation upsertPhotos($input: UpsertPhotosInput!) {
    upsertPhotos(input: $input) {
      id
    }
  }
`;
